import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Edit, Trash2, Search, Menu, ArrowUp, ArrowDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { menuService } from '../../services';

const MenuMaster = () => {
  const [menus, setMenus] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  // Load menus from API
  useEffect(() => {
    loadMenus();
  }, []);

  const loadMenus = async () => {
    try {
      setLoading(true);
      const response = await menuService.getAll({ search: searchTerm });
      setMenus(response.data || []);
    } catch (error) {
      console.error('Error loading menus:', error);
      toast.error('Failed to load menus');
    } finally {
      setLoading(false);
    }
  };

  // Reload menus when search term changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadMenus();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const openModal = (menu = null) => {
    setEditingMenu(menu);
    setShowModal(true);
    if (menu) {
      reset({
        name: menu.name,
        path: menu.path,
        order: menu.order,
        is_active: menu.is_active
      });
    } else {
      reset({ name: '', path: '', order: '', is_active: true });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingMenu(null);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (editingMenu) {
        // Update menu
        await menuService.update(editingMenu.id, data);
        toast.success('Menu updated successfully!');
      } else {
        // Create new menu
        await menuService.create(data);
        toast.success('Menu created successfully!');
      }
      closeModal();
      loadMenus(); // Reload the list
    } catch (error) {
      console.error('Error saving menu:', error);
      toast.error('Failed to save menu');
    } finally {
      setLoading(false);
    }
  };

  const deleteMenu = async (id) => {
    if (window.confirm('Are you sure you want to delete this menu?')) {
      try {
        setLoading(true);
        await menuService.delete(id);
        toast.success('Menu deleted successfully!');
        loadMenus(); // Reload the list
      } catch (error) {
        console.error('Error deleting menu:', error);
        toast.error('Failed to delete menu');
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleStatus = async (menu) => {
    try {
      setLoading(true);
      await menuService.update(menu.id, { 
        name: menu.name,
        path: menu.path,
        order: menu.order,
        is_active: !menu.is_active 
      });
      toast.success('Menu status updated!');
      loadMenus(); // Reload the list
    } catch (error) {
      console.error('Error updating menu status:', error);
      toast.error('Failed to update menu status');
    } finally {
      setLoading(false);
    }
  };

  const moveMenu = async (id, direction) => {
    const currentIndex = menus.findIndex(menu => menu.id === id);
    const newMenus = [...menus];
    
    if (direction === 'up' && currentIndex > 0) {
      [newMenus[currentIndex], newMenus[currentIndex - 1]] = [newMenus[currentIndex - 1], newMenus[currentIndex]];
    } else if (direction === 'down' && currentIndex < menus.length - 1) {
      [newMenus[currentIndex], newMenus[currentIndex + 1]] = [newMenus[currentIndex + 1], newMenus[currentIndex]];
    }
    
    // Update order values
    newMenus.forEach((menu, index) => {
      menu.order = index + 1;
    });
    
    setMenus(newMenus);
    
    // In a real app, you'd update the order in the backend
    try {
      await menuService.update(id, { 
        name: newMenus[currentIndex === 0 ? 1 : currentIndex - 1].name,
        path: newMenus[currentIndex === 0 ? 1 : currentIndex - 1].path,
        order: newMenus[currentIndex === 0 ? 1 : currentIndex - 1].order,
        is_active: newMenus[currentIndex === 0 ? 1 : currentIndex - 1].is_active
      });
      toast.success('Menu order updated!');
    } catch (error) {
      console.error('Error updating menu order:', error);
      toast.error('Failed to update menu order');
      loadMenus(); // Reload to reset on error
    }
  };

  return (
    <div>
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>
            Menu Master
          </h1>
          <button
            onClick={() => openModal()}
            className="btn btn-primary"
          >
            <Plus size={18} />
            Add Menu
          </button>
        </div>

        <div style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '20px'
        }}>
          <p style={{ color: '#0369a1', fontSize: '14px', margin: 0 }}>
            💡 <strong>Tip:</strong> Use the arrow buttons to reorder menu items. The order here determines how they appear in your navigation.
          </p>
        </div>

        {/* Search */}
        <div style={{ marginBottom: '20px', position: 'relative', maxWidth: '300px' }}>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6b7280'
            }}
          />
          <input
            type="text"
            placeholder="Search menus..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-box"
            style={{ paddingLeft: '40px' }}
          />
        </div>

        {/* Menus Table */}
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Menu Name</th>
                <th>Path</th>
                <th>Order</th>
                <th>Status</th>
                <th>Created Date</th>
                <th>Updated Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {menus.map((menu, index) => (
                <tr key={menu.id}>
                  <td>{menu.id}</td>
                  <td style={{ fontWeight: '500' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Menu size={16} color="#6b7280" />
                      {menu.name}
                    </div>
                  </td>
                  <td>
                    <code style={{
                      fontFamily: 'monospace',
                      backgroundColor: '#f3f4f6',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      {menu.path || '-'}
                    </code>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{
                        backgroundColor: '#f3f4f6',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {menu.order}
                      </span>
                      <div style={{ display: 'flex', gap: '2px' }}>
                        <button
                          onClick={() => moveMenu(menu.id, 'up')}
                          disabled={index === 0 || loading}
                          className="btn btn-secondary"
                          style={{
                            padding: '2px 4px',
                            fontSize: '10px',
                            opacity: index === 0 ? 0.5 : 1
                          }}
                          title="Move Up"
                        >
                          <ArrowUp size={10} />
                        </button>
                        <button
                          onClick={() => moveMenu(menu.id, 'down')}
                          disabled={index === menus.length - 1 || loading}
                          className="btn btn-secondary"
                          style={{
                            padding: '2px 4px',
                            fontSize: '10px',
                            opacity: index === menus.length - 1 ? 0.5 : 1
                          }}
                          title="Move Down"
                        >
                          <ArrowDown size={10} />
                        </button>
                      </div>
                    </div>
                  </td>
                  <td>
                    <button
                      onClick={() => toggleStatus(menu)}
                      className={`status-badge ${menu.is_active ? 'status-active' : 'status-inactive'}`}
                      style={{ cursor: 'pointer', border: 'none' }}
                      disabled={loading}
                    >
                      {menu.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>{new Date(menu.created_at).toLocaleDateString()}</td>
                  <td>{new Date(menu.updated_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => openModal(menu)}
                        className="btn btn-secondary"
                        style={{ padding: '6px 8px', fontSize: '12px' }}
                        disabled={loading}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => deleteMenu(menu.id)}
                        className="btn btn-danger"
                        style={{ padding: '6px 8px', fontSize: '12px' }}
                        disabled={loading}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            Loading menus...
          </div>
        )}

        {!loading && menus.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            No menus found.
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingMenu ? 'Edit Menu' : 'Add New Menu'}
              </h2>
              <button onClick={closeModal} className="close-btn">
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group">
                <label className="form-label">Menu Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter menu name"
                  {...register('name', {
                    required: 'Menu name is required',
                    minLength: {
                      value: 2,
                      message: 'Menu name must be at least 2 characters'
                    }
                  })}
                />
                {errors.name && (
                  <div className="error-message">{errors.name.message}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Menu Path</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter menu path (e.g. /for-him, /for-her)"
                  {...register('path', {
                    required: 'Menu path is required',
                    pattern: {
                      value: /^\/[a-z0-9-]+$/,
                      message: 'Path must start with / and contain only lowercase letters, numbers, and hyphens'
                    }
                  })}
                />
                {errors.path && (
                  <div className="error-message">{errors.path.message}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Order</label>
                <input
                  type="number"
                  className="form-input"
                  placeholder="Enter display order"
                  min="1"
                  {...register('order', {
                    required: 'Order is required',
                    min: {
                      value: 1,
                      message: 'Order must be at least 1'
                    }
                  })}
                />
                {errors.order && (
                  <div className="error-message">{errors.order.message}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  {...register('is_active', { required: 'Status is required' })}
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </select>
                {errors.is_active && (
                  <div className="error-message">{errors.is_active.message}</div>
                )}
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingMenu ? 'Update Menu' : 'Create Menu'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuMaster;